import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import ProductAccessValidator from '../../components/auth/ProductAccessValidator';
import { AthenaProvider } from '../../contexts';
import ProjectsListPage from './ProjectsListPage';
import ProjectWorkspace from './ProjectWorkspace';
import {
  OverviewTab,
  TaraWorkflowTab,
  ArchitectureTab,
  UNR155ChecklistTab,
  IntegrationsTab,
  ConfigurationTab,
  AttackTreesTab,
  ReportsTab,
  VersionsTab,
  TraceabilityTab,
  TraceabilityMapTab,
  ResidualTaraTab,
  ComplianceMappingTab,
  AttackTreeEditorTab,
  ArchitectureDiagramEditorTab,
} from './components/WorkspaceTabs';

/**
 * Top-level project-management routes (NOT nested under /athena/*).
 * Both routes require Athena product access; ProjectWorkspace also
 * needs the AthenaProvider for in-workspace TARA flows.
 *
 * FU-001 (2026-05-27): ProjectWorkspace migrated from `?tab=` query
 * param to React-Router child routes with `<Outlet />`. Each tab is
 * now a named URL segment under `/project/:projectId/*`; the default
 * Overview tab is the `index` route (URL stays clean — no `/overview`
 * segment shown for the default).
 */
export const projectsRoutes = (
  <>
    <Route
      path="/projects"
      element={
        <ProductAccessValidator requiredProduct="athena">
          <ProjectsListPage />
        </ProductAccessValidator>
      }
    />
    <Route
      path="/project/:projectId"
      element={
        <ProductAccessValidator requiredProduct="athena">
          <AthenaProvider>
            <ProjectWorkspace />
          </AthenaProvider>
        </ProductAccessValidator>
      }
    >
      <Route index element={<OverviewTab />} />
      <Route path="overview" element={<Navigate to=".." replace />} />
      <Route path="tara-workflow" element={<TaraWorkflowTab />} />
      {/* Epic 12 retire shim — bookmarks pointing at the v2-tab alias
          land on the canonical `tara-workflow` route. */}
      <Route path="tara-workflow-v2" element={<Navigate to="../tara-workflow" replace />} />
      <Route path="architecture" element={<ArchitectureTab />} />
      {/* Nested editor routes (siblings of their list tabs). The
          active-tab indicator in ProjectHeader stays on the parent
          segment (`architecture` / `attack-trees`) because
          activeProjectTab derives from `pathSegments[2]`. */}
      <Route path="architecture/:diagramId" element={<ArchitectureDiagramEditorTab />} />
      <Route path="attack-trees" element={<AttackTreesTab />} />
      <Route path="attack-trees/:threatId" element={<AttackTreeEditorTab />} />
      <Route path="residual-tara" element={<ResidualTaraTab />} />
      <Route path="unr155-checklist" element={<UNR155ChecklistTab />} />
      <Route path="compliance-mapping" element={<ComplianceMappingTab />} />
      <Route path="traceability" element={<TraceabilityTab />} />
      <Route path="traceability-map" element={<TraceabilityMapTab />} />
      <Route path="reports" element={<ReportsTab />} />
      <Route path="versions" element={<VersionsTab />} />
      <Route path="integrations" element={<IntegrationsTab />} />
      <Route path="configuration" element={<ConfigurationTab />} />
      {/* Unknown segments fall back to Overview (preserves the
          legacy `?tab=` invalid-value behaviour). */}
      <Route path="*" element={<OverviewTab />} />
    </Route>
  </>
);
